/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { type QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import type { PaymentRequestFormInput } from '@web/src/features/admin/payment-request-form/payment-request-form'
import PaymentRequestForm from '@web/src/features/admin/payment-request-form/payment-request-form'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { UPDATE_PAYMENT_REQUEST } from '@web/src/lib/mutations'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type { EditPaymentRequestQuery, EditPaymentRequestQueryVariables } from 'types/graphql'

const EDIT_PAYMENT_REQUEST_QUERY: TypedDocumentNode<EditPaymentRequestQuery, EditPaymentRequestQueryVariables> = gql`
  query EditPaymentRequestQuery($paymentRequestId: String!) {
    paymentRequest(id: $paymentRequestId) {
      id
      profileId
      days
      partnerIds
      total
    }
  }
`

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/payments/requests/$paymentRequestId/edit')({
  component: EditPaymentRequestPage,
  head: () =>
    seo({
      title: `Edit Payment Request | ${env.VITE_APP_NAME}`,
      description: 'Edit payment request',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: ({ context, params }) => {
    const queryRef = context.preloadQuery(EDIT_PAYMENT_REQUEST_QUERY, {
      variables: { paymentRequestId: params.paymentRequestId },
    })
    return { queryRef }
  },
})

function EditPaymentRequestPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { paymentRequestId } = Route.useParams()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditPaymentRequestQuery>)
  const [showSpinner, setShowSpinner] = useState(false)

  const [updatePaymentRequest] = useMutation(UPDATE_PAYMENT_REQUEST, {
    onCompleted: () => {
      toast.success(t('Payment request updated'))
      setShowSpinner(false)
      navigate({ to: '/admin/payments', search: { tab: 'pending' } } as never)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinner(false)
    },
  })

  const onSave = (input: PaymentRequestFormInput) => {
    setShowSpinner(true)
    updatePaymentRequest({
      variables: {
        id: paymentRequestId,
        input: {
          days: input.days,
          partnerIds: input.partnerIds,
          total: input.total,
        },
      },
    })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) return <SFPageLoading />
  if (error) return <ErrorAlert error={error} />

  const pr = data?.paymentRequest
  if (!pr) return <ErrorAlert error={new Error(t('Payment request not found'))} />

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col gap-4">
        <h1 className="w-full text-center font-semibold text-3xl tracking-tight">{t('Edit Payment Request')}</h1>
        <PaymentRequestForm
          initialValues={{
            profileId: pr.profileId,
            days: pr.days,
            partnerIds: pr.partnerIds ?? [],
            total: pr.total,
          }}
          mode="edit"
          onSave={onSave}
          showSpinner={showSpinner}
        />
      </div>
    </div>
  )
}
